import React, { useState, forwardRef, useMemo } from 'react'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import StaticDatePicker from '@mui/lab/StaticDatePicker'
import { Divider, Button, Typography, TextField, Paper } from '@mui/material'
import PickersDay from '@mui/lab/PickersDay'
import { isSameDay, getDay } from 'date-fns'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { usePaletteTheme } from '../../context/paletteTheme'
import { commonColors } from '../../utils/constants'
import styles from './CustomCalendar.module.scss'

const getStyle = (day, isFirstDay, isLastDay, sameDay, dayIsBetween) => {
  let style = ''

  if (dayIsBetween && !sameDay) {
    if (isFirstDay) style+= `${styles['selected-first-day']} `
    else if (isLastDay) style+= `${styles['selected-last-day']} `
    else style+= `${styles['selected-day']} `
  
    if(day === 0 && !isFirstDay) style+= `${styles['radius-left']} `
    else if(day === 6  && !isLastDay) style+= `${styles['radius-right']} `
  }
  return style
}

const CustomCalendar = forwardRef(({initialDate, handleClose, handleCalendarSubmit},ref) => {
  const today = new Date()
  const minDate = Date.UTC(2000, 0, 0)
  const [start, setStart] = useState(new Date(initialDate.start ?? [today.getFullYear(), today.getMonth(), today.getDate()]))
  const [end, setEnd] = useState(new Date(initialDate.end ?? today))

  const { theme } = usePaletteTheme()
  const handleSubmit = () => handleCalendarSubmit({start, end})

  const calendarTheme = useMemo(() => createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        main: commonColors.calendar,
      },
    }
  }), [theme])

  const renderDaysBetween = (date, _, pickersDayProps) => {
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())

    const dayIsBetween = date >= startDay && date <= end
    const isFirstDay = isSameDay(date, startDay)
    const isLastDay = isSameDay(date, end)
    const sameDay = isSameDay(startDay, end)
    const pickerDay = getDay(pickersDayProps.day)
    const pickerDayStyle = getStyle(pickerDay, isFirstDay, isLastDay, sameDay, dayIsBetween)

    return (
      <PickersDay
        disableMargin
        classes={{root: `${styles['root']} ${pickerDayStyle}`}} {...pickersDayProps}
      />
    )
  }
  
    return (
      <Paper elevation={2} classes={{root: styles['paper']}}>
        <div className={styles['calendar']}>
          <ThemeProvider theme={calendarTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div>
                <Typography variant='h2' align='center'>Start Date</Typography>
                <Divider/>
                <StaticDatePicker 
                  classes={{viewTransitionContainer: styles['viewTransitionContainer']}} 
                  date={start}
                  minDate={minDate}
                  maxDate={end}
                  onChange={(newDate) => setStart(newDate)}
                  renderDay={renderDaysBetween}
                  showToolbar={false}
                  renderInput={(params) => <TextField {...params} />}
                />
              </div>
              <div>
                <Typography variant='h2' align='center'>End Date</Typography>
                <Divider/>
                <StaticDatePicker
                  classes={{viewTransitionContainer: styles['viewTransitionContainer']}} 
                  date={end}
                  minDate={start}
                  maxDate={today}
                  renderDay={renderDaysBetween}
                  onChange={(newDate) => setEnd(newDate)}
                  showToolbar={false}
                  renderInput={(params) => <TextField {...params} />}
                />
              </div>
            </LocalizationProvider>
          </ThemeProvider>
        </div>
        <div className={styles['buttons']}>
          <Button onClick={handleClose} classes={{root: styles['cancel-btn']}}>CANCEL</Button>
          <Button onClick={handleSubmit} variant="contained" classes={{root: styles['select-btn']}}>SELECT</Button>
        </div>
      </Paper>
    )
  })

  export default CustomCalendar